import {
  Box,
  Button,
  Card,
  Collapsible,
  HStack,
  IconButton,
  NativeSelect,
  SegmentGroup,
  SimpleGrid,
  SkeletonText,
  Stack,
  Stat,
  Switch,
  Table,
} from '@chakra-ui/react'
import { useMemo, useState, type ReactNode } from 'react'
import { LuCircleAlert, LuClipboardList, LuMinus, LuPlus } from 'react-icons/lu'
import { Link as RouterLink } from 'react-router-dom'
import type { Order } from '@/common/order.common'
import { WORKLOAD_SERVICES } from '@/components/common/WorkloadQuotaCard'
import { YearPager } from '@/components/order/YearPager'
import { EmptyStateCard } from '@/components/widget-wrappers/EmptyStateCard'
import { WidgetShell } from '@/components/widget-wrappers/WidgetShell'
import { getHoursPerUnit } from '@/helper/order.helper'
import {
  SERVICE_CODES,
  useSibCapacityOverview,
  type SibCapacityOverview,
  type SibServiceCode,
  type UnitValues,
} from './useSibCapacityOverview'
import { serviceLabel } from './serviceDisplay'
import { WorkProgressChart } from './WorkProgressChart'
import { buildWorkProgress } from './workProgress'

export type SibCapacityRecipient = { id: string; label: string }

type Props = {
  userId: string | undefined
  /** The Union's recipients (associated entities), for display names by stable id. */
  recipients: SibCapacityRecipient[]
  /** Injected in tests; defaults to now. */
  today?: Date
}

const OVERVIEW = 'Übersicht'
const percentFormat = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 })
const formatPercent = (v: number | null | undefined) =>
  v == null ? '–' : `${percentFormat.format(v)} %`

const MONTHS = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
]

const numberFormat = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 })
const dateFormat = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
})

const serviceInfo = (code: SibServiceCode) => WORKLOAD_SERVICES.find(s => s.key === code)!

function formatUnits(code: SibServiceCode, value: number | null | undefined) {
  return value == null ? '–' : `${numberFormat.format(value)} ${serviceInfo(code).unit}`
}

function formatDate(iso: string) {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '–' : dateFormat.format(d)
}

/**
 * Planned hours of an order in the service's unit (display conversion only), with the catalogue's
 * hours per unit like every other conversion in this widget.
 */
function orderedUnits(order: Order, hoursPerUnit: Record<string, number>): UnitValues {
  const out: UnitValues = {}
  for (const b of order.buckets ?? []) {
    const code = (b.serviceType === 'TOJ_CUSTOMER' ? 'TOJ' : b.serviceType) as SibServiceCode
    if (!SERVICE_CODES.includes(code)) continue
    const units = (b.plannedHours ?? 0) / getHoursPerUnit(code, hoursPerUnit[code])
    out[code] = Math.round(((out[code] ?? 0) + units) * 100) / 100
  }
  return out
}

function servicesWithValues(...values: UnitValues[]): SibServiceCode[] {
  return SERVICE_CODES.filter(code => values.some(v => (v[code] ?? 0) !== 0))
}

/**
 * The SIB's "Arbeitszeit" widget (SIB-792): year progress against the order schedules (Mode B,
 * SIB-730) with an optional projection, followed by the capacity cards - quota and free capacity per
 * service, approved orders with their end dates, what remains per recipient, and this month's
 * activity totals. The cards show backend figures; the progress chart is derived from the SIB's
 * orders and activities as described in workProgress.ts.
 */
export function SibCapacityWidget({ userId, recipients, today }: Props) {
  const { state, year, setYear, minYear, maxYear, retry } = useSibCapacityOverview(userId, today)

  return (
    <WidgetShell
      data-testid="time-tracking-widget"
      title="Arbeitszeit"
      headerRight={
        <YearPager
          year={year}
          minYear={minYear}
          maxYear={maxYear}
          onChange={setYear}
          testIdPrefix="sib-capacity-year"
        />
      }
    >
      {state.status === 'loading' && (
        <Stack gap="section" aria-busy="true" aria-label="Arbeitszeit wird geladen">
          <SkeletonText noOfLines={3} />
          <SkeletonText noOfLines={4} />
        </Stack>
      )}

      {state.status === 'error' && (
        <EmptyStateCard
          icon={<LuCircleAlert />}
          title="Arbeitszeit konnte nicht geladen werden"
          description="Die Daten sind derzeit nicht verfügbar. Bitte versuchen Sie es erneut."
          action={
            <Button variant="outline" onClick={() => retry()}>
              Erneut versuchen
            </Button>
          }
        />
      )}

      {state.status === 'ready' && (
        <Stack gap="section">
          <ProgressSection data={state.data} recipients={recipients} today={today ?? new Date()} />
          <CapacityContent data={state.data} recipients={recipients} />
        </Stack>
      )}
    </WidgetShell>
  )
}

function CapacityContent({
  data,
  recipients,
}: {
  data: SibCapacityOverview
  recipients: SibCapacityRecipient[]
}) {
  const recipientName = (id: number) =>
    recipients.find(r => r.id === String(id))?.label ?? `Leistungsempfänger ${id}`
  const quotaMissing = !data.quotaKnown || data.services.every(s => s.quota == null)

  return (
    <Stack gap="section">
      <CollapsibleCard
        id="quota"
        label="Kontingent und freie Kapazität"
        title={`Kontingent und freie Kapazität ${data.year}`}
      >
        {quotaMissing ? (
          <EmptyStateCard
            icon={<LuClipboardList />}
            title="Kein Kontingent hinterlegt"
            description={`Für ${data.year} ist kein Leistungskontingent hinterlegt.`}
            action={
              <Button asChild variant="outline">
                <RouterLink to="/settings/orders">Kontingent eintragen</RouterLink>
              </Button>
            }
          />
        ) : (
          <SimpleGrid columns={{ base: 2, md: 4 }} gap="stack">
            {data.services.map(s => (
              <Stat.Root key={s.code} minW={0}>
                <Stat.Label>{serviceInfo(s.code).label}</Stat.Label>
                <Stat.ValueText>
                  {s.quota == null ? 'Nicht hinterlegt' : formatUnits(s.code, s.quota)}
                </Stat.ValueText>
                <Stat.HelpText>
                  {s.quota == null ? 'Kein Kontingent' : `frei: ${formatUnits(s.code, s.free)}`}
                </Stat.HelpText>
              </Stat.Root>
            ))}
          </SimpleGrid>
        )}
      </CollapsibleCard>

      {data.currentMonth && (
        <CollapsibleCard
          id="month"
          label="Aktivitäten im laufenden Monat"
          title={`Aktivitäten im ${MONTHS[data.currentMonth.month - 1]} ${data.year}`}
        >
          <SimpleGrid columns={{ base: 2, md: 4 }} gap="stack">
            {SERVICE_CODES.map(code => (
              <Stat.Root key={code} minW={0}>
                <Stat.Label>{serviceInfo(code).label}</Stat.Label>
                <Stat.ValueText>
                  {formatUnits(code, data.currentMonth!.performed[code] ?? 0)}
                </Stat.ValueText>
              </Stat.Root>
            ))}
          </SimpleGrid>
        </CollapsibleCard>
      )}

      <CollapsibleCard
        id="orders"
        label="Freigegebene Bestellungen"
        title={`Freigegebene Bestellungen ${data.year}`}
      >
        {data.orders.length === 0 ? (
          <EmptyStateCard
            icon={<LuClipboardList />}
            title="Keine freigegebenen Bestellungen"
            description={`Für ${data.year} liegt keine freigegebene Bestellung vor.`}
          />
        ) : (
          <Table.ScrollArea>
            <Table.Root>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>Leistungsempfänger</Table.ColumnHeader>
                  <Table.ColumnHeader>Bestellt</Table.ColumnHeader>
                  <Table.ColumnHeader>Beginn</Table.ColumnHeader>
                  <Table.ColumnHeader>Ende</Table.ColumnHeader>
                  <Table.ColumnHeader>Status</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {data.orders.map(order => {
                  const units = orderedUnits(order, data.hoursPerUnit)
                  return (
                    <Table.Row key={order.id}>
                      <Table.Cell>
                        {Number(order.customerId ?? 0) > 0
                          ? recipientName(Number(order.customerId))
                          : '–'}
                      </Table.Cell>
                      <Table.Cell>
                        {servicesWithValues(units)
                          .map(
                            code => `${serviceInfo(code).label} ${formatUnits(code, units[code])}`
                          )
                          .join(' · ') || '–'}
                      </Table.Cell>
                      <Table.Cell>{formatDate(order.startTime)}</Table.Cell>
                      <Table.Cell>{formatDate(order.endTime)}</Table.Cell>
                      <Table.Cell>
                        {order.status === 'COMPLETED' ? 'Abgeschlossen' : 'Freigegeben'}
                      </Table.Cell>
                    </Table.Row>
                  )
                })}
              </Table.Body>
            </Table.Root>
          </Table.ScrollArea>
        )}
      </CollapsibleCard>

      {data.recipients.length > 0 && (
        <CollapsibleCard
          id="recipients"
          label="Verbleibende Leistungen je Leistungsempfänger"
          title={`Verbleibende Leistungen je Leistungsempfänger ${data.year}`}
        >
          <Card.Description>
            Bestellt (anteilig für {data.year}) abzüglich geleisteter Aktivitäten. Abgerechnete
            Mengen werden nicht zusätzlich abgezogen.
          </Card.Description>
          <Table.ScrollArea>
            <Table.Root>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>Leistungsempfänger</Table.ColumnHeader>
                  <Table.ColumnHeader>Leistung</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="end">Bestellt</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="end">Geleistet</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="end">Verbleibend</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {data.recipients.flatMap(r =>
                  servicesWithValues(r.ordered, r.performed).map(code => (
                    <Table.Row key={`${r.customerId}-${code}`}>
                      <Table.Cell>{recipientName(r.customerId)}</Table.Cell>
                      <Table.Cell>{serviceInfo(code).label}</Table.Cell>
                      <Table.Cell textAlign="end">
                        {formatUnits(code, r.ordered[code] ?? 0)}
                      </Table.Cell>
                      <Table.Cell textAlign="end">
                        {formatUnits(code, r.performed[code] ?? 0)}
                      </Table.Cell>
                      <Table.Cell textAlign="end">
                        {formatUnits(code, r.remaining[code] ?? 0)}
                      </Table.Cell>
                    </Table.Row>
                  ))
                )}
              </Table.Body>
            </Table.Root>
          </Table.ScrollArea>
        </CollapsibleCard>
      )}
    </Stack>
  )
}

type CardId = 'quota' | 'month' | 'orders' | 'recipients'

// Which capacity cards the user has collapsed, per browser. A convenience only: when storage is
// unavailable every card simply starts open.
const COLLAPSED_CARDS_KEY = 'dashboard.workload.collapsedCards.v1'

function readCollapsedCards(): CardId[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(COLLAPSED_CARDS_KEY) ?? '[]')
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function writeCollapsedCards(ids: CardId[]) {
  try {
    localStorage.setItem(COLLAPSED_CARDS_KEY, JSON.stringify(ids))
  } catch {
    // Storage blocked or full - the card still toggles for this visit.
  }
}

/** A capacity card whose content can be collapsed to its title, like "Verlauf" in Auftragsvolumen. */
function CollapsibleCard({
  id,
  label,
  title,
  children,
}: {
  id: CardId
  /** Names the card in the toggle's accessible label, without the year. */
  label: string
  title: ReactNode
  children: ReactNode
}) {
  const [open, setOpen] = useState(() => !readCollapsedCards().includes(id))

  const toggle = (next: boolean) => {
    setOpen(next)
    const collapsed = new Set(readCollapsedCards())
    if (next) collapsed.delete(id)
    else collapsed.add(id)
    writeCollapsedCards([...collapsed])
  }

  return (
    <Card.Root variant="subtle">
      <Collapsible.Root open={open} onOpenChange={e => toggle(e.open)}>
        <Card.Body>
          <Stack gap="stack">
            <HStack justify="space-between" gap="inline">
              <Card.Title>{title}</Card.Title>
              <Collapsible.Trigger asChild>
                <IconButton
                  variant="ghost"
                  aria-label={`${label} ${open ? 'ausblenden' : 'anzeigen'}`}
                  aria-expanded={open}
                >
                  {open ? <LuMinus /> : <LuPlus />}
                </IconButton>
              </Collapsible.Trigger>
            </HStack>
            <Collapsible.Content>
              <Stack gap="stack">{children}</Stack>
            </Collapsible.Content>
          </Stack>
        </Card.Body>
      </Collapsible.Root>
    </Card.Root>
  )
}

function ProgressSection({
  data,
  recipients,
  today,
}: {
  data: SibCapacityOverview
  recipients: SibCapacityRecipient[]
  today: Date
}) {
  const [tab, setTab] = useState<string>(OVERVIEW)
  const [recipientId, setRecipientId] = useState<number | null>(null)
  const [showProjection, setShowProjection] = useState(true)
  const service =
    tab === OVERVIEW ? null : (SERVICE_CODES.find(c => serviceLabel(c) === tab) ?? null)

  // Recipients that have an order this year, by stable id.
  const orderRecipients = useMemo(
    () =>
      [...new Set(data.orders.map(o => Number(o.customerId ?? 0)).filter(id => id > 0))].map(
        id => ({
          id,
          label: recipients.find(r => r.id === String(id))?.label ?? `Leistungsempfänger ${id}`,
        })
      ),
    [data.orders, recipients]
  )
  const selectedRecipient = orderRecipients.some(r => r.id === recipientId) ? recipientId : null

  const todayKey = today.toDateString()
  const progress = useMemo(
    () =>
      buildWorkProgress({
        year: data.year,
        today,
        orders: data.orders,
        activities: data.activities,
        recipientId: selectedRecipient,
        hoursPerUnit: data.hoursPerUnit,
      }),
    // `today` is compared by day.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, selectedRecipient, todayKey]
  )
  const running = progress.todayIndex != null
  const isFuture = data.year > today.getFullYear()
  const lastActual = progress.todayIndex ?? progress.days - 1
  const shown = progress.services.filter(
    s => s.ordered > 0 && (service == null || s.code === service)
  )

  return (
    <Card.Root variant="subtle">
      <Card.Body>
        <Stack gap="stack">
          <Card.Title>Fortschritt {data.year}</Card.Title>
          <HStack gap="stack" wrap="wrap">
            {/* Five tabs are wider than a phone; let them scroll instead of the page. */}
            <Box overflowX="auto" maxW="full">
              <SegmentGroup.Root value={tab} onValueChange={e => setTab(e.value ?? OVERVIEW)}>
                <SegmentGroup.Indicator />
                <SegmentGroup.Items items={[OVERVIEW, ...SERVICE_CODES.map(serviceLabel)]} />
              </SegmentGroup.Root>
            </Box>
            {orderRecipients.length > 1 && (
              <NativeSelect.Root>
                <NativeSelect.Field
                  aria-label="Leistungsempfänger"
                  value={selectedRecipient ?? ''}
                  onChange={e => setRecipientId(e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">Alle Leistungsempfänger</option>
                  {orderRecipients.map(r => (
                    <option key={r.id} value={r.id}>
                      {r.label}
                    </option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            )}
            {running && shown.length > 0 && (
              <Switch.Root
                checked={showProjection}
                onCheckedChange={e => setShowProjection(e.checked)}
              >
                <Switch.HiddenInput />
                <Switch.Control />
                <Switch.Label>Prognose anzeigen</Switch.Label>
              </Switch.Root>
            )}
          </HStack>

          {shown.length === 0 ? (
            <EmptyStateCard
              icon={<LuClipboardList />}
              title="Kein Soll-Verlauf"
              description={
                service
                  ? `Für ${serviceLabel(service)} liegt ${data.year} keine freigegebene Bestellung vor.`
                  : `Für ${data.year} liegt keine freigegebene Bestellung vor.`
              }
            />
          ) : (
            <>
              <WorkProgressChart
                progress={progress}
                service={service}
                showProjection={running && showProjection}
              />
              <Table.ScrollArea>
                <Table.Root aria-label={`Fortschritt ${data.year} je Leistung`}>
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader>Leistung</Table.ColumnHeader>
                      <Table.ColumnHeader textAlign="end">Bestellt</Table.ColumnHeader>
                      <Table.ColumnHeader textAlign="end">Geleistet</Table.ColumnHeader>
                      <Table.ColumnHeader textAlign="end">Fortschritt</Table.ColumnHeader>
                      <Table.ColumnHeader textAlign="end">
                        {running ? 'Soll heute' : 'Soll'}
                      </Table.ColumnHeader>
                      {running && showProjection && (
                        <Table.ColumnHeader textAlign="end">Prognose 31.12.</Table.ColumnHeader>
                      )}
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {shown.map(s => (
                      <Table.Row key={s.code}>
                        <Table.Cell>{serviceLabel(s.code)}</Table.Cell>
                        <Table.Cell textAlign="end">{formatUnits(s.code, s.ordered)}</Table.Cell>
                        <Table.Cell textAlign="end">
                          {isFuture ? '–' : formatUnits(s.code, s.completedUnits)}
                        </Table.Cell>
                        <Table.Cell textAlign="end">
                          {isFuture ? '–' : formatPercent(s.completed[lastActual])}
                        </Table.Cell>
                        <Table.Cell textAlign="end">
                          {formatPercent(isFuture ? 0 : s.target[lastActual])}
                        </Table.Cell>
                        {running && showProjection && (
                          <Table.Cell textAlign="end">
                            {formatPercent(s.projection[progress.days - 1])}
                          </Table.Cell>
                        )}
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              </Table.ScrollArea>
            </>
          )}
        </Stack>
      </Card.Body>
    </Card.Root>
  )
}
